import { useCallback, useEffect, useMemo, useState } from 'react';
import { ARCHIVE_CARRY_OVER } from '@orgazone/core-nf525';
import { Badge, Button, EmptyState, Icon, useLatest } from '@orgazone/ui-web';
import { api, type ArchiveIntegrity, type FiscalArchive, type FiscalArchivesView } from '../api';
import { useStoreScope } from '../store-context';
import { currentLang } from '../lang';
import { moisLongLisible } from '../periode';
import { key, t } from '../i18n';

/** Fond, bordure et rayon communs aux sections — le dessin de carte du back-office. */
const CARTE: React.CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
  padding: 16, marginBottom: 16,
};

/** Un contrôle de saisie de la barre de constitution. */
const CHAMP: React.CSSProperties = {
  height: 36, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', fontFamily: 'inherit',
};

/** L'étiquette au-dessus d'un contrôle. */
function Etiquette({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ display: 'block', marginBottom: 4, fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
      {children}
    </span>
  );
}

/** Les deux périodicités que le serveur accepte — liste FERMÉE, côté serveur comme ici. */
const PERIODICITES = [
  { id: 'mensuelle' as const, label: key('Mensuelle') },
  { id: 'annuelle' as const, label: key('Annuelle') },
];

/**
 * ARCHIVES FISCALES — la chaîne d'archives du magasin, et ce qui empêche d'en constituer une.
 *
 * UTILITÉ : c'est l'écran qui répond à « mes archives sont-elles en règle, et puis-je archiver le
 * mois dernier ? » (NF525 §6.9).
 *
 * ⚠️ 🔴 LE VERDICT VIENT DU SERVEUR, RECALCULÉ À CHAQUE LECTURE — l'écran ne juge rien lui-même.
 * Une santé mémorisée ne dirait rien de l'altération d'hier, et c'est précisément elle qu'il faut
 * voir.
 *
 * ⚠️ 🔴 LE RECOUPEMENT EST MONTRÉ AVEC LE CHAÎNAGE MAIS COMPTÉ À PART, et ce n'est pas une
 * coquetterie : prises séparément, les deux chaînes restent PARFAITES après la disparition de leur
 * dernier maillon — elles ne se coupent pas au même endroit. C'est leur DÉSACCORD qui est le signal.
 *
 * ⚠️ LE TÉLÉCHARGEMENT REMET UN ZIP DES FICHIERS SCELLÉS, et le serveur en CONTRÔLE l'intégrité avant
 * de le produire : une archive qui ne se vérifie plus est REFUSÉE avec son motif, plutôt que remise à
 * quelqu'un qui la conserverait six ans en croyant être en règle. Sa sortie est tracée au JET 440.
 *
 * ⚠️ CE QUI N'EST PAS ICI, ET POURQUOI. La PURGE après archivage (§6.10.2) : aucun mécanisme serveur
 * ne l'exécute, et un bouton qui promettrait de libérer de la place sans rien purger serait un bouton
 * qui fait semblant (§23.0). La ROTATION de la clé de signature : elle existe au module, n'a aucune
 * route, et reste de toute façon un geste d'ÉDITEUR — pas de commerçant. Ce qui n'est pas branché
 * s'enlève, il ne se simule pas.
 */
export function Archives() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [vue, setVue] = useState<FiscalArchivesView | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [rapports, setRapports] = useState<Record<string, ArchiveIntegrity>>({});
  const [controle, setControle] = useState<string | null>(null);
  const [remise, setRemise] = useState<string | null>(null);
  const [periodicite, setPeriodicite] = useState<'mensuelle' | 'annuelle'>('mensuelle');
  const [mois, setMois] = useState(() => moisPrecedent());
  const [annee, setAnnee] = useState(() => String(new Date().getFullYear() - 1));

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT L'ÉCRAN : un magasin changé pendant la lecture la relance, et la chaîne de
  //    l'ancien, revenue en dernier, s'afficherait sous le bandeau du nouveau.
  const latest = useLatest();
  const charger = useCallback(() => {
    if (!gln) return;
    const isLatest = latest();
    setChargement(true);
    setErreur(null);
    api.fiscalArchives()
      .then((v) => { if (!isLatest()) return; setVue(v); setChargement(false); })
      // ⚠️ LE MESSAGE RÉEL, et l'état de succès NON appliqué : une chaîne qu'on n'a pas pu lire ne
      //    doit jamais ressembler à un magasin sans archive (§23.0).
      .catch((e: unknown) => {
        if (!isLatest()) return;
        setVue(null);
        setErreur(e instanceof Error ? e.message : String(e));
        setChargement(false);
      });
  }, [gln, latest]);

  useEffect(charger, [charger]);

  const constituer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const { from, to } = bornes(periodicite, mois, annee);
      await api.constituteArchive(periodicite, from, to);
      charger();
    } catch (e) {
      // ⚠️ C'EST ICI QUE LE REFUS DE CLÔTURE ARRIVE, et il s'affiche TEL QUEL : le serveur nomme les
      //    caisses dont le grand total manque. Le résumer ferait perdre la seule information qui dit
      //    quoi faire ensuite.
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnCours(false);
    }
  };

  const verifier = async (a: FiscalArchive) => {
    setControle(a.archiveId);
    setErreur(null);
    try {
      const r = await api.verifyArchive(a.archiveId);
      setRapports((p) => ({ ...p, [a.archiveId]: r }));
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setControle(null);
    }
  };

  /**
   * RÉCUPÉRER UNE ARCHIVE — le serveur la contrôle, l'empaquette et trace sa sortie ; on enregistre.
   *
   * ⚠️ LE NOM DU FICHIER VIENT DU SERVEUR, jamais de l'écran : c'est lui qui connaît le rang dans la
   * chaîne, et ce nom finit dans un dossier que le commerçant relira dans six ans. Le repli n'existe
   * que pour le cas où l'en-tête n'arrive pas, et il est reconnaissable comme tel.
   */
  const recuperer = async (a: FiscalArchive) => {
    setRemise(a.archiveId);
    setErreur(null);
    try {
      const { blob, filename } = await api.downloadArchive(a.archiveId);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = filename ?? `archive-fiscale_${a.periodFrom}-${a.periodTo}.zip`;
      lien.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // ⚠️ UN REFUS S'AFFICHE, il ne se télécharge pas : `client.download` décode l'enveloppe
      //    d'erreur, donc une archive altérée dit POURQUOI au lieu de produire un fichier illisible.
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setRemise(null);
    }
  };

  const verdict = useMemo(() => (vue ? verdictDeLaChaine(vue) : null), [vue]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            {t('Archives fiscales')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            {t('Chaque archive est signée et chaînée à la précédente : en retirer une casse la vérification de toutes les suivantes.')}
          </p>
        </div>
      </div>

      {erreur ? (
        <div role="alert" style={{ ...CARTE, display: 'flex', gap: 10, alignItems: 'flex-start', borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}>
          <Icon name="alert" size={17} stroke={2.1} />
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)', whiteSpace: 'pre-wrap' }}>{erreur}</span>
        </div>
      ) : null}

      <section style={CARTE}>
        <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {t('Constituer une archive')}
        </h2>
        <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
          {t('La période doit être CLOSE : chaque caisse qui a vendu doit avoir produit son grand total. Sans cela l’archive partirait amputée du mois d’une caisse, sans que rien ne le dise.')}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '0 1 170px', minWidth: 150 }}>
            <Etiquette>{t('Périodicité')}</Etiquette>
            <select
              value={periodicite}
              onChange={(e) => setPeriodicite(e.target.value as 'mensuelle' | 'annuelle')}
              style={{ ...CHAMP, width: '100%' }}
            >
              {PERIODICITES.map((p) => <option key={p.id} value={p.id}>{t(p.label)}</option>)}
            </select>
          </label>
          <label style={{ flex: '0 1 190px', minWidth: 160 }}>
            <Etiquette>{periodicite === 'annuelle' ? t('Année') : t('Mois')}</Etiquette>
            {periodicite === 'annuelle' ? (
              <input
                type="number" min="2000" max="2100" step="1" value={annee}
                onChange={(e) => setAnnee(e.target.value)}
                aria-label={t('Année à archiver')}
                style={{ ...CHAMP, width: '100%', fontVariantNumeric: 'tabular-nums' }}
              />
            ) : (
              <input
                type="month" value={mois} onChange={(e) => setMois(e.target.value)}
                aria-label={t('Mois à archiver')}
                style={{ ...CHAMP, width: '100%', fontVariantNumeric: 'tabular-nums' }}
              />
            )}
          </label>
          <Button
            variant="primary" icon="lock" onClick={() => void constituer()}
            disabled={enCours || !gln}
            reason={!gln ? t('Aucun magasin actif.') : undefined}
          >
            {enCours ? t('Constitution…') : t('Constituer')}
          </Button>
        </div>
      </section>

      <section style={CARTE}>
        <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {t('État de la chaîne')}
        </h2>

        {chargement ? (
          <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture de la chaîne…')}</p>
        ) : !vue ? null : vue.archives.length === 0 ? (
          <EmptyState
            icon="box"
            title={t('Aucune archive pour ce magasin')}
            note={t('Rien n’a encore été archivé. La première archive ouvre la chaîne : les suivantes s’y rattachent, et c’est ce chaînage qui rend une disparition visible.')}
          />
        ) : (
          <>
            {verdict ? <VerdictChaine verdict={verdict} verifiees={vue.chain.checked} /> : null}
            <TableauArchives
              archives={vue.archives} rapports={rapports} controle={controle} remise={remise}
              onVerifier={(a) => void verifier(a)}
              onRecuperer={(a) => void recuperer(a)}
            />
          </>
        )}
      </section>

      {vue && vue.keys.length > 0 ? <ClesDeVerification keys={vue.keys} /> : null}
    </>
  );
}

/**
 * LE VERDICT EN UNE LIGNE — vert seulement si les DEUX contrôles du serveur concordent.
 *
 * ⚠️ « Rien à signaler » n'est affiché que quand le SERVEUR l'affirme : un vert qui résumerait « la
 * plupart des archives sont saines » ne servirait à rien sur une chaîne fiscale.
 */
function VerdictChaine({ verdict, verifiees }: { verdict: VerdictChaineArchives; verifiees: number }) {
  const ko = !verdict.ok;
  const soucis = verdict.lignes;
  return (
    <div style={{
      display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 14, padding: '9px 12px',
      borderRadius: 'var(--r-sm)',
      background: ko ? 'var(--danger-bg)' : 'var(--teal-100)',
      color: ko ? 'var(--ink-900)' : 'var(--teal-800)',
    }}>
      <Icon name={ko ? 'alert' : 'check'} size={16} stroke={2.2} />
      <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>
        {ko
          ? soucis.join(' · ')
          : t('{n} archive vérifiée : chaînage, signatures et journal concordent.', { n: verifiees })}
      </span>
    </div>
  );
}

/** L'en-tête d'une colonne du tableau. */
const TH: React.CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontSize: '11.5px', fontWeight: 700,
  letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--ink-500)',
  borderBottom: '1px solid var(--ink-200)', whiteSpace: 'nowrap',
};

/** Une cellule du tableau. */
const TD: React.CSSProperties = {
  padding: '9px 10px', fontSize: 'var(--t-body)', color: 'var(--ink-800)',
  borderBottom: '1px solid var(--ink-100)', verticalAlign: 'middle',
};

/** LES ARCHIVES, la plus récente en tête — et le contrôle d'intégrité de chacune. */
function TableauArchives({ archives, rapports, controle, remise, onVerifier, onRecuperer }: {
  archives: FiscalArchive[];
  rapports: Record<string, ArchiveIntegrity>;
  controle: string | null;
  remise: string | null;
  onVerifier: (a: FiscalArchive) => void;
  onRecuperer: (a: FiscalArchive) => void;
}) {
  // ⚠️ DU PLUS RÉCENT AU PLUS ANCIEN : le registre rend la chaîne dans son ordre de constitution
  //    (c'est ce que la vérification exige), mais on lit un tableau par le haut.
  const lignes = [...archives].sort((a, b) => b.seq - a.seq);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>{t('Rang')}</th>
            <th style={TH}>{t('Période')}</th>
            <th style={TH}>{t('Constituée le')}</th>
            <th style={TH}>{t('Report')}</th>
            <th style={{ ...TH, textAlign: 'right' }}>{t('Fichiers')}</th>
            <th style={TH}>{t('Intégrité')}</th>
            {/* ⚠️ 🔴 PAS « Remise » : ce mot est DÉJÀ une clé du catalogue, au sens COMMERCIAL — la
                colonne se serait intitulée « Discount » en anglais, et aucune porte ne l'aurait vu
                (la clé existe, elle est traduite, la complétude est satisfaite). Le français étant la
                clé, un homonyme hérite en silence de l'autre sens. */}
            <th style={TH}>{t('Récupération')}</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((a) => (
            <tr key={a.archiveId}>
              <td style={{ ...TD, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{a.seq}</td>
              <td style={TD}>{periodeLisible(a)}</td>
              <td style={{ ...TD, fontVariantNumeric: 'tabular-nums' }}>{horodatageLisible(a.constitutedAt)}</td>
              {/* ⚠️ Le report est une valeur ÉNUMÉRÉE, pas un vide : un champ blanc ne distinguerait
                  pas « première de la chaîne » de « report perdu ». */}
              <td style={TD}>
                {a.carryOver === ARCHIVE_CARRY_OVER.FIRST
                  ? <Badge tone="neutral">{t('première de la chaîne')}</Badge>
                  : <Badge tone="info">{t('chaînée à la précédente')}</Badge>}
              </td>
              <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a.fileCount}</td>
              <td style={TD}><CelluleIntegrite a={a} r={rapports[a.archiveId]} enCours={controle === a.archiveId} onVerifier={onVerifier} /></td>
              <td style={TD}>
                {/* ⚠️ LE SERVEUR CONTRÔLE L'INTÉGRITÉ AVANT DE REMETTRE : le bouton n'a donc pas à
                    exiger un contrôle préalable à l'écran — une archive altérée est REFUSÉE, avec
                    son motif. Exiger le clic sur « Contrôler » d'abord donnerait à croire que
                    l'inverse est possible. */}
                {remise === a.archiveId
                  ? <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Préparation…')}</span>
                  : <Button size="sm" icon="download" onClick={() => onRecuperer(a)}>{t('Télécharger')}</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * L'INTÉGRITÉ D'UNE ARCHIVE — non contrôlée, intacte, ou fautive avec ses motifs.
 *
 * ⚠️ TROIS ÉTATS, JAMAIS DEUX : « pas encore contrôlée » n'est pas « intacte ». Confondre les deux
 * afficherait un vert sur une archive que personne n'a relue.
 */
function CelluleIntegrite({ a, r, enCours, onVerifier }: {
  a: FiscalArchive; r: ArchiveIntegrity | undefined; enCours: boolean;
  onVerifier: (a: FiscalArchive) => void;
}) {
  if (enCours) return <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Contrôle…')}</span>;
  if (!r) return <Button size="sm" icon="shield" onClick={() => onVerifier(a)}>{t('Contrôler')}</Button>;
  if (r.ok) return <Badge tone="ok" dot>{t('intacte')}</Badge>;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <Badge tone="danger" dot>{t('altérée')}</Badge>
      {/* ⚠️ TOUTES les fautes, jamais la première seule : l'écran est le seul endroit où le rapport
          complet existe (le descriptif du JET est borné, donc tronqué). */}
      <span style={{ fontSize: '12px', color: 'var(--ink-700)' }}>{r.faults.join(' · ')}</span>
    </span>
  );
}

/**
 * LES CLÉS PUBLIQUES — ce avec quoi un contrôleur vérifie sans nous.
 *
 * ⚠️ UNE CLÉ RETIRÉE RESTE AFFICHÉE : les archives qu'elle a signées doivent rester vérifiables, et
 * la faire disparaître donnerait à croire qu'elles ne le sont plus.
 */
function ClesDeVerification({ keys }: { keys: { keyId: string; publicKey: string; algo: string; isActive: boolean }[] }) {
  return (
    <section style={CARTE}>
      <h2 style={{ margin: '0 0 6px', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('Clés de vérification')}
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
        {t('Ces clés PUBLIQUES permettent de vérifier une archive sans nous — c’est ce qui rend l’archive exploitable après nous.')}
      </p>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {keys.map((k) => (
          <li key={k.keyId} style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge form="code" tone="neutral">{k.keyId}</Badge>
            <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>{k.algo}</span>
            {k.isActive
              ? <Badge tone="ok" dot>{t('en service')}</Badge>
              : <Badge tone="neutral">{t('retirée du service')}</Badge>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Ce que l'écran affiche du verdict : la décision, et de quoi l'expliquer. */
export interface VerdictChaineArchives { ok: boolean; lignes: string[] }

/**
 * LE VERDICT DE LA CHAÎNE — la DÉCISION vient du serveur, les phrases ne font que l'expliquer.
 *
 * ⚠️ 🔴 ON NE RECALCULE PAS `ok` À PARTIR DES LISTES DE DÉTAIL, et c'est tout l'objet de cette
 * fonction. Le serveur pose son verdict (`chain.ok`, `crossCheck.ok`) ; le déduire ici de la longueur
 * des listes ferait exister DEUX définitions de « la chaîne est saine » — la sienne et la nôtre. Elles
 * coïncident aujourd'hui, et c'est justement ce qui rend l'écart dangereux : le jour où le serveur
 * ajoute une famille de fautes, l'écran afficherait VERT sur une chaîne que le serveur déclare rompue,
 * sans qu'aucune porte ne le voie (Forme A sur un verdict).
 *
 * ⚠️ D'où la ligne de repli : un défaut affirmé SANS détail se DIT. Se taire reviendrait à montrer un
 * bandeau rouge vide, et retomber sur le vert à afficher l'inverse de ce que le serveur a constaté.
 */
export function verdictDeLaChaine(vue: FiscalArchivesView): VerdictChaineArchives {
  const ok = vue.chain.ok && vue.crossCheck.ok;
  const lignes = defauts(vue);
  if (!ok && lignes.length === 0) {
    lignes.push(t('Un défaut est signalé sur la chaîne sans qu’aucun détail ne l’accompagne — faites contrôler les archives une à une.'));
  }
  return { ok, lignes };
}

/**
 * LES DÉFAUTS DE LA CHAÎNE, en phrases — chaînage ET recoupement au journal.
 *
 * ⚠️ LES DEUX FAMILLES SONT DISTINCTES : un chaînage parfait ne dit rien d'une troncature en queue,
 * et c'est le recoupement au JET qui la voit. Les fondre en un seul « la chaîne est cassée » ferait
 * perdre l'information qui désigne où chercher.
 */
export function defauts(vue: FiscalArchivesView): string[] {
  const out: string[] = [];
  if (vue.chain.breaks.length) out.push(t('{n} maillon rompu dans la chaîne', { n: vue.chain.breaks.length }));
  if (vue.chain.badSignatures.length) out.push(t('{n} signature invalide', { n: vue.chain.badSignatures.length }));
  if (vue.chain.missingSeq.length) {
    out.push(t('{n} rang manquant au milieu de la chaîne ({v})', { n: vue.chain.missingSeq.length, v: vue.chain.missingSeq.join(', ') }));
  }
  if (vue.crossCheck.archivesSansEvenement.length) {
    out.push(t('{n} archive sans son événement au journal', { n: vue.crossCheck.archivesSansEvenement.length }));
  }
  if (vue.crossCheck.evenementsSansArchive.length) {
    out.push(t('{n} événement de journal sans son archive', { n: vue.crossCheck.evenementsSansArchive.length }));
  }
  return out;
}

/** Le mois précédent, au format `AAAA-MM` de l'input natif. */
export function moisPrecedent(maintenant: Date = new Date()): string {
  const d = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Une période que l'écran refuse de convertir — le message part tel quel à l'opérateur. */
export class PeriodeIllisible extends Error {}

/**
 * Les bornes d'une période, en `AAAAMMJJ` — le format que la route exige.
 *
 * ⚠️ LE DERNIER JOUR SE CALCULE, il ne se devine pas : `new Date(a, m, 0)` rend le dernier jour du
 * mois `m`, années bissextiles comprises. Une table de 30/31 se tromperait un février sur quatre.
 *
 * ⚠️ 🔴 ON REFUSE, ON NE CORRIGE PAS. Un champ `month` vidé au clavier rend `''`, et le calcul
 * naïf en tirait `NaN0101` — une borne que la route rejette avec un message de FORMAT, pendant que
 * l'écran laissait croire qu'une période avait été demandée. Le refus nomme ce qui manque.
 */
export function bornes(kind: 'mensuelle' | 'annuelle', mois: string, annee: string): { from: string; to: string } {
  const p2 = (n: number) => String(n).padStart(2, '0');
  if (kind === 'annuelle') {
    if (!/^\d{4}$/.test(annee.trim())) throw new PeriodeIllisible(t('Indiquez l’année à archiver (quatre chiffres).'));
    return { from: `${annee.trim()}0101`, to: `${annee.trim()}1231` };
  }
  const found = /^(\d{4})-(\d{2})$/.exec(mois.trim());
  if (!found) throw new PeriodeIllisible(t('Indiquez le mois à archiver.'));
  const a = Number(found[1]);
  const m = Number(found[2]);
  if (m < 1 || m > 12) throw new PeriodeIllisible(t('Mois hors de l’année : {v}.', { v: mois }));
  const dernier = new Date(a, m, 0).getDate();
  return { from: `${a}${p2(m)}01`, to: `${a}${p2(m)}${p2(dernier)}` };
}

/**
 * La période d'une archive, en clair.
 *
 * ⚠️ LA CAPITALE EST AJOUTÉE : `Intl` rend « septembre » et « September » — la minuscule est correcte
 * en français dans une phrase, pas en tête de cellule.
 */
export function periodeLisible(a: Pick<FiscalArchive, 'periodKind' | 'periodFrom'>, lang: string = currentLang()): string {
  const an = a.periodFrom.slice(0, 4);
  if (a.periodKind === 'annuelle') return an;
  // ⚠️ DÉLÉGUÉ à l'atelier des périodes : le centre de notifications nomme le MÊME mois, et deux
  //    rendus du même fait auraient divergé à la première correction (règle de 2).
  const s = moisLongLisible(a.periodFrom.slice(0, 6), lang);
  // ⚠️ LA CAPITALE EST AJOUTÉE ICI ET NON DANS L’ATELIER : `Intl` rend « septembre », juste au milieu
  //    d’une phrase et faux en tête de cellule. C’est donc l’usage qui tranche, pas le rendu.
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * `AAAAMMJJHHMMSS` rendu lisiblement.
 *
 * ⚠️ 🔴 C'EST UNE HEURE LOCALE DU MAGASIN, déjà convertie à la constitution : on la découpe, on ne la
 * reconstruit JAMAIS en instant. La relire comme une date la replacerait dans le fuseau du LECTEUR,
 * et une archive constituée à 00h30 à Paris se daterait de la veille pour un lecteur à l'ouest.
 */
export function horodatageLisible(s: string): string {
  if (!/^\d{14}$/.test(s)) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}
